import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useDownloadReport,
  useServiceOrderHistoryList,
  useServiceOrderHistorySummary,
} from "../../../hooks";
import {
  ServiceOrderHistoryDisplayItem,
  ServiceOrderHistoryFilterPanelFilters,
} from "../../../types";
import { ServiceOrderHistoryFilterPanel } from "../filters/serviceOrderHistoryFilterPanel";
import ServiceOrderHistoryDashboard from "./dashboard/ServiceOrderHistoryDashboard";

const ServiceOrderHistory = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isExporting, setIsExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = PAGE_SIZE;
  const downloadCSV = useDownloadReport();

  // Extract current params from URL - Following ServiceOrdersTab pattern
  const currentSearch = decodeURIComponent(searchParams.get("search") || "");
  const currentStatus = searchParams.get("status") || "";
  const currentPriority = searchParams.get("priority") || "";
  const currentOrderType = searchParams.get("orderType") || "";
  const currentAssignedTo = searchParams.get("assignedTo") || "";
  const currentDateFrom = searchParams.get("dateFrom") || "";
  const currentDateTo = searchParams.get("dateTo") || "";

  // Use individual parameters instead of filter object - Following ServiceOrdersTab pattern
  const { data: historyResponse, isLoading } = useServiceOrderHistoryList(
    currentSearch,
    currentStatus,
    currentPriority,
    currentOrderType,
    currentAssignedTo,
    currentPage,
    PAGE_SIZE
  );

  const { data: summaryData, isLoading: summaryLoading } =
    useServiceOrderHistorySummary();

  // Build currentFilters object for filter panel - Following ServiceOrdersTab pattern
  const currentFilters = useMemo(
    () => ({
      status: currentStatus,
      priority: currentPriority,
      assignedTo: currentAssignedTo,
      orderType: currentOrderType,
      dateRange:
        currentDateFrom && currentDateTo
          ? {
              from: new Date(currentDateFrom),
              to: new Date(currentDateTo),
            }
          : undefined,
    }),
    [
      currentStatus,
      currentPriority,
      currentAssignedTo,
      currentOrderType,
      currentDateFrom,
      currentDateTo,
    ]
  );

  const getServiceTypeFromAssociation = (associationType: string): string => {
    if (associationType?.includes("Asset")) return "Asset";
    if (associationType?.includes("Facility")) return "Facility";
    if (associationType?.includes("System")) return "System";
    if (associationType?.includes("Network")) return "Network";
    return "Asset"; // Default fallback
  };

  // Pagination info from API or calculate from data - Following ServiceOrdersTab pattern
  const pagination = useMemo(() => {
    const totalItems = historyResponse?.count || 0;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startItem = totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return {
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage,
      startItem,
      endItem,
    };
  }, [currentPage, historyResponse]);

  // Handlers - Following ServiceOrdersTab pattern
  const handleSearch = (searchTerm: string) => {
    const newParams: any = { ...Object.fromEntries(searchParams.entries()) };
    if (searchTerm.trim()) {
      newParams.search = encodeURIComponent(searchTerm.trim());
    } else {
      delete newParams.search;
    }
    newParams.page = 1;
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    const newParams: any = { ...Object.fromEntries(searchParams.entries()) };
    newParams.page = page.toString();
    setSearchParams(newParams);
    setCurrentPage(page);
  };

  const handleApplyFilters = (
    filters: ServiceOrderHistoryFilterPanelFilters
  ) => {
    const newParams: any = { ...Object.fromEntries(searchParams.entries()) };

    // Update URL params like ServiceOrdersTab
    if (filters.status && filters.status !== "all") {
      newParams.status = filters.status;
    } else {
      delete newParams.status;
    }

    if (filters.priority && filters.priority !== "all") {
      newParams.priority = filters.priority;
    } else {
      delete newParams.priority;
    }

    if (filters.assignedTo && filters.assignedTo !== "all") {
      newParams.assignedTo = filters.assignedTo;
    } else {
      delete newParams.assignedTo;
    }

    if (filters.orderType && filters.orderType !== "all") {
      newParams.orderType = filters.orderType;
    } else {
      delete newParams.orderType;
    }

    newParams.page = 1;
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    const newParams: any = { ...Object.fromEntries(searchParams.entries()) };
    delete newParams.status;
    delete newParams.priority;
    delete newParams.assignedTo;
    delete newParams.orderType;
    delete newParams.dateFrom;
    delete newParams.dateTo;
    delete newParams.search;
    newParams.page = 1;
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  const handleExport = () => {
    setIsExporting(true);
    setTimeout(() => setIsExporting(false), 1000);
  };

  const handleDownload = (id: any) => {
    if (!id) return;
    downloadCSV.mutateAsync({
      reportId: Number(id),
      tabName: "completed",
    });
  };

  const tableColumns: TableColumn<any>[] = [
    {
      key: "jobCardNo",
      header: "SO ID",
      sortable: true,
      headerTooltip: "Unique, system-generated ID",
    },
    {
      key: "sopName",
      header: "SO Name",
      sortable: true,
      headerTooltip: "The name of the service order.",
    },
    {
      key: "sopAssociationTypeDisplay",
      header: "Service Association",
      sortable: true,
      headerTooltip:
        "The type of entity the order is for (Asset, System, Facility, Network).",
    },
    {
      key: "requestAccountNo",
      header: "Ref ID/Name",
      sortable: true,
      type: "custom",
      render: (value: string, row: any) => (
        <div>
          <div>{value}</div>
          <div className="text-sm text-gray-500 ">
            {row.requestUsername ?? "NA"}
          </div>
        </div>
      ),
      headerTooltip: "The ID and name of the specific entity.",
    },
    {
      key: "sopOperationTypeDisplay",
      header: "Type",
      sortable: true,
      headerTooltip:
        "The category of work or maintenance operation that this work order covers (e.g., Preventive Maintenance, Repair, Inspection, Emergency Response).",
    },
    {
      key: "completedDate",
      header: "Completed Date",
      sortable: true,
      headerTooltip: "The date the service order was completed.",
    },
    {
      key: "fieldForceName",
      header: "Completed By",
      sortable: true,
      headerTooltip: "The field force member assigned to the order.",
    },
    {
      key: "totalActualCost",
      header: "Total Cost",
      sortable: true,
      headerTooltip: "The total cost of the service order.",
    },
    {
      key: "statusDisplay",
      header: "Status",
      sortable: true,
      type: "badge", // This was missing!
      badgeConfig: {
        getClassName: getStatusBadgeClass,
      },
      headerTooltip: "The current status of the service order.",
    },
  ];

  const tableActions = [
    {
      label: "View Details",
      onClick: (row: ServiceOrderHistoryDisplayItem) => {
        const serviceType = getServiceTypeFromAssociation(
          row.sopAssociationTypeDisplay
        );
        navigate(
          `/ax/service-order/landing/history/view/${serviceType}/${row.id}`
        );
      },
      variant: "ghost" as const,
    },
    {
      label: "Print/Download Report",
      onClick: (row: ServiceOrderHistoryDisplayItem) => {
        handleDownload(row.id);
      },
      variant: "ghost" as const,
    },
  ];

  // Check if there are active filters - Following ServiceOrdersTab pattern
  const hasActiveFilters =
    currentSearch ||
    currentStatus ||
    currentPriority ||
    currentOrderType ||
    currentAssignedTo ||
    currentDateFrom ||
    currentDateTo;

  return (
    <div className="min-h-screen bg-gray-50 bg-white">
      <div className="max-w-[1400px] mx-auto space-y-6">
        {/* Header Section */}

        <ServiceOrderHistoryDashboard summaryData={summaryData} />

        <GenericTable
          data={historyResponse?.results || []}
          columns={tableColumns}
          rowKey="id"
          actions={tableActions}
          actionsType="dropdown"
          loading={isLoading}
          onSearch={handleSearch}
          searchPlaceholder="Search by SO ID or Name..."
          searchParamName="search"
          searchDebounceMs={300}
          filterPanel={
            <ServiceOrderHistoryFilterPanel
              currentFilters={currentFilters}
              onApplyFilters={handleApplyFilters}
              onResetFilters={handleResetFilters}
            />
          }
          pagination={pagination}
          onPageChange={handlePageChange}
          onExport={handleExport}
          exportLoading={isExporting}
          emptyMessage="No service order history found matching your criteria"
          filters={
            hasActiveFilters ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Filters active
                </span>
              </div>
            ) : null
          }
        />
      </div>
    </div>
  );
};

export default ServiceOrderHistory;
